import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { ContentContainer } from "@/components/primitives/ContentContainer";
import { SectionHeader } from "@/components/primitives/SectionHeader";
import { Card, CardTitle, CardDescription } from "@/components/primitives/Card";
import { MetricCard } from "@/components/primitives/MetricCard";
import { Callout } from "@/components/primitives/Callout";
import { MediaPlaceholder } from "@/components/primitives/MediaPlaceholder";
import { QuoteBlock } from "@/components/primitives/QuoteBlock";
import { Timeline } from "@/components/primitives/Timeline";
import { ChartPlaceholder } from "@/components/primitives/ChartPlaceholder";
import { Expandable } from "@/components/primitives/Expandable";
import { Button } from "@/components/primitives/Button";
import { Modal } from "@/components/primitives/Modal";
import { ProgressIndicator } from "@/components/primitives/ProgressIndicator";
import {
  SLIDE_CATALOGUE,
  HeroSlide,
  ResearchSlide,
  ObservationSlide,
  ComparisonSlide,
  ProductWalkthroughSlide,
  MetricsDashboardSlide,
  TimelineSlide,
  QuoteSlide,
  BusinessModelSlide,
  FounderSlide,
  ClosingSlide,
} from "@/components/presentation/slides";
import { cn } from "@/lib/cn";

const SLIDE_RENDERERS = {
  hero: () => <HeroSlide onContinue={() => {}} />,
  research: () => <ResearchSlide />,
  observation: () => <ObservationSlide />,
  comparison: () => <ComparisonSlide />,
  "product-walkthrough": () => <ProductWalkthroughSlide />,
  "metrics-dashboard": () => <MetricsDashboardSlide />,
  timeline: () => <TimelineSlide />,
  quote: () => <QuoteSlide commentary="Optional supporting commentary, kept brief." />,
  "business-model": () => <BusinessModelSlide />,
  founder: () => <FounderSlide />,
  closing: () => <ClosingSlide onCta={() => {}} onEnterExplore={() => {}} />,
};

const SlideStage = ({ slide }) => {
  const render = SLIDE_RENDERERS[slide.id];
  return (
    <article
      data-testid={`slide-stage-${slide.id}`}
      className="overflow-hidden rounded-xl border border-border bg-card"
    >
      <header className="flex items-center justify-between gap-4 border-b border-border bg-surface/40 px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
            {String(SLIDE_CATALOGUE.findIndex((s) => s.id === slide.id) + 1).padStart(2, "0")}
          </span>
          <div>
            <div className="text-sm font-medium text-foreground">{slide.title}</div>
            <div className="text-xs text-muted-foreground">{slide.description}</div>
          </div>
        </div>
        <span className="hidden font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground sm:inline">
          {slide.id}
        </span>
      </header>
      <div className="px-6 py-12 sm:px-10 sm:py-16">
        {render ? render() : <div className="text-sm text-muted-foreground">Coming soon.</div>}
      </div>
    </article>
  );
};

const Group = ({ title, description, children, testId }) => (
  <section
    data-testid={testId}
    className="border-t border-border py-16 first:border-t-0 first:pt-0"
  >
    <div className="mb-10 max-w-2xl">
      <div className="haevn-eyebrow mb-3">{title}</div>
      {description && (
        <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
      )}
    </div>
    {children}
  </section>
);

const Swatch = ({ name, varName, className }) => (
  <div className="flex flex-col gap-2">
    <div
      className={cn(
        "h-16 w-full rounded-lg border border-border",
        className
      )}
      style={{ background: `hsl(var(${varName}))` }}
    />
    <div className="flex items-center justify-between text-xs">
      <span className="font-medium text-foreground">{name}</span>
      <span className="font-mono text-muted-foreground">{varName}</span>
    </div>
  </div>
);

const TypeRow = ({ label, sample, classes }) => (
  <div className="flex flex-col gap-2 border-b border-border py-5 last:border-b-0 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8">
    <div className="flex shrink-0 flex-col">
      <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </span>
      <span className="mt-0.5 font-mono text-xs text-muted-foreground/70">
        {classes}
      </span>
    </div>
    <div className={cn("text-foreground", classes)}>{sample}</div>
  </div>
);

export default function Components() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <ContentContainer>
      <SectionHeader
        eyebrow="Design System"
        title="The components that build every section."
        description="A living reference for the HAEVN investor experience. Every primitive listed below will be reused — not reinvented — in future iterations."
      />

      <div className="mt-16">
        <Group
          title="Color"
          description="A neutral palette with a single restrained accent. Both themes derive from CSS tokens so themes never drift."
          testId="ds-color"
        >
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-6">
            <Swatch name="Background" varName="--background" />
            <Swatch name="Surface" varName="--surface" />
            <Swatch name="Foreground" varName="--foreground" />
            <Swatch name="Muted" varName="--muted-foreground" />
            <Swatch name="Border" varName="--border" />
            <Swatch name="Brand" varName="--brand" />
          </div>
        </Group>

        <Group
          title="Typography"
          description="Geist. Hierarchy through size, spacing, and weight — never decoration."
          testId="ds-typography"
        >
          <div className="rounded-xl border border-border bg-card px-8">
            <TypeRow
              label="Display"
              sample="Calm, confident interface."
              classes="text-4xl sm:text-5xl font-medium tracking-tightest"
            />
            <TypeRow
              label="Heading"
              sample="Section heading"
              classes="text-2xl sm:text-3xl font-medium tracking-tight"
            />
            <TypeRow
              label="Subheading"
              sample="Subheading example"
              classes="text-lg font-medium tracking-tight"
            />
            <TypeRow
              label="Body"
              sample="Body copy reads at sixteen pixels with relaxed line height for sustained reading."
              classes="text-base leading-relaxed text-muted-foreground"
            />
            <TypeRow
              label="Small"
              sample="Small meta text and labels."
              classes="text-sm text-muted-foreground"
            />
            <TypeRow
              label="Mono"
              sample="01 · 07"
              classes="font-mono text-sm tabular-nums text-muted-foreground"
            />
          </div>
        </Group>

        <Group
          title="Buttons"
          description="Three variants. Always paired with a single, clear action."
          testId="ds-buttons"
        >
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-8">
            <Button variant="primary" data-testid="ds-button-primary">
              Primary
            </Button>
            <Button variant="secondary" data-testid="ds-button-secondary">
              Secondary
            </Button>
            <Button variant="ghost" data-testid="ds-button-ghost">
              Ghost
            </Button>
            <Button variant="brand" data-testid="ds-button-brand">
              Brand accent
            </Button>
            <Button
              variant="primary"
              iconRight={<ArrowRight className="h-4 w-4" />}
              data-testid="ds-button-with-icon"
            >
              With icon
            </Button>
          </div>
        </Group>

        <Group title="Metric cards" testId="ds-metrics">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard label="Revenue" value="$4.28M" delta="+12.4%" trend="up" hint="MoM" />
            <MetricCard label="Customers" value="1,284" delta="+86" trend="up" hint="Net new" />
            <MetricCard label="Churn" value="1.2%" delta="-0.3pp" trend="down" hint="MoM" />
            <MetricCard label="NPS" value="62" hint="Q4" />
          </div>
        </Group>

        <Group title="Callouts" testId="ds-callouts">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Callout variant="info" title="Information">A neutral, factual statement.</Callout>
            <Callout variant="note" title="Note">Quiet emphasis on a detail.</Callout>
            <Callout variant="warning" title="Caution">Use sparingly for risk or sensitivity.</Callout>
            <Callout variant="success" title="Confirmed">Positive confirmation of a state.</Callout>
          </div>
        </Group>

        <Group title="Cards" testId="ds-cards">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} padding="lg" interactive testId={`ds-card-${i}`}>
                <CardTitle>Card title</CardTitle>
                <CardDescription className="mt-2">
                  A standard content card. Borders, radius, and padding match every
                  other surface in the system.
                </CardDescription>
              </Card>
            ))}
          </div>
        </Group>

        <Group title="Media placeholders" testId="ds-media">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <MediaPlaceholder kind="image" />
            <MediaPlaceholder kind="video" />
          </div>
        </Group>

        <Group title="Quote" testId="ds-quote">
          <QuoteBlock
            quote="Design that stays out of the way is the highest form of design."
            attribution="HAEVN"
            role="Design principle"
          />
        </Group>

        <Group title="Timeline" testId="ds-timeline">
          <Timeline
            items={[
              { date: "01", title: "First milestone", body: "Body copy placeholder.", active: true },
              { date: "02", title: "Second milestone", body: "Body copy placeholder." },
              { date: "03", title: "Third milestone", body: "Body copy placeholder." },
            ]}
          />
        </Group>

        <Group title="Chart placeholder" testId="ds-chart">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ChartPlaceholder variant="line" />
            <ChartPlaceholder variant="bars" />
          </div>
        </Group>

        <Group title="Expandable" testId="ds-expandable">
          <div className="flex flex-col gap-2">
            <Expandable title="First expandable row" defaultOpen testId="ds-expandable">
              Reveals a paragraph of supplementary detail without leaving the current
              context. Useful for FAQs and progressive disclosure.
            </Expandable>
            <Expandable title="Second expandable row" testId="ds-expandable">
              Stays closed by default. Animates open with a calm ease-out curve.
            </Expandable>
          </div>
        </Group>

        <Group title="Progress" testId="ds-progress">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <ProgressIndicator value={28} label="Onboarding" />
            <ProgressIndicator value={64} label="Build" />
            <ProgressIndicator value={92} label="Launch" />
          </div>
        </Group>

        <Group title="Modal" testId="ds-modal">
          <div className="rounded-xl border border-border bg-card p-8">
            <Button onClick={() => setModalOpen(true)} data-testid="ds-open-modal">
              Open modal
            </Button>
          </div>
          <Modal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            title="Modal title"
            description="A focused dialog for a single action."
          >
            <p className="text-sm leading-relaxed text-muted-foreground">
              Modals are reserved for confirmations and focused capture. They are not
              used as a replacement for routing.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => setModalOpen(false)}>Confirm</Button>
            </div>
          </Modal>
        </Group>

        <Group
          title="Presentation slides"
          description="Reusable narrative building blocks. Each slide renders with placeholder content so future chapters can compose without creating new layouts."
          testId="ds-slides"
        >
          <div
            className="flex flex-col gap-6"
            data-testid="ds-slide-catalogue"
          >
            {SLIDE_CATALOGUE.map((slide) => (
              <SlideStage key={slide.id} slide={slide} />
            ))}
          </div>
        </Group>
      </div>
    </ContentContainer>
  );
}
